import { AuthenticTracking } from './authentic-tracking.js'

class ThemeGa4 extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.trackFilterItems()
    this.trackProductCardViews()
    this.trackVideos()
    this.trackRemoveFromCart()
    this.trackNewsletterSectionViews()
    this.trackCustomerLogin()
    this.trackSearchItemRedirects()

    const hasAuthenticTrigger = document.querySelector(
      '[data-drawers-trigger="authentic-info"]',
    )

    if (hasAuthenticTrigger) {
      const tracking = new AuthenticTracking()
      tracking.init()
    }
  }

  setupListeners () {
    super.setupListeners()

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      () => {
        this.trackRemoveFromCart()
      },
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.DRAWER.OPENED,
      this.trackDrawers.bind(this),
    )

    const recommendationWrappers = document.querySelectorAll(
      '[data-ga4-recs-el]',
    )
    if (recommendationWrappers && recommendationWrappers.length > 0) {
      Array.from(recommendationWrappers).forEach(recsWrapper => {
        recsWrapper.addEventListener(
          'click',
          this.trackRecsInteractions.bind(this),
        )
      })
    }

    const sortButtons = document.querySelectorAll(
      '[data-ga4-el="plp-sort-button"]',
    )
    if (sortButtons && sortButtons.length > 0) {
      Array.from(sortButtons).forEach(btn => {
        btn.addEventListener('click', this.trackPlpSort)
      })
    }

    const sortSelect = document.querySelector('[data-ga4-el="plp-sort-select"]')
    if (sortSelect) {
      sortSelect.addEventListener('change', this.trackPlpSort)
    }
  }

  trackVideos () {
    const videoEls = document.querySelectorAll(`[data-theme-ga4-el='video']`)

    videoEls.forEach(videoEl => {
      this.listeners.add(videoEl, 'play', this.handleTrackVideo.bind(this))
      this.listeners.add(videoEl, 'ended', this.handleTrackVideo.bind(this))
    })
  }

  handleTrackVideo (event) {
    window.dataLayer.push({
      page_type: 'video_interaction',
      event: 'video_interaction',
      video_src: `${event.target.dataset.themeGa4Src}`,
      video_progress: event.type,
    })
  }

  trackProductCardViews () {
    // Watch for collection page load mores
    this.onCollectionPaginationChange = this.onCollectionPaginationChange.bind(
      this,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.AFTER_PAGINATION_CHANGE,
      this.onCollectionPaginationChange,
    )

    // Product card observer
    const productCardEls = document.querySelectorAll(
      `[data-theme-ga4-el='product-card']`,
    )

    this.observer = new IntersectionObserver(
      this.trackProductCardViewEntry.bind(this),
      {
        threshold: 0.8,
      },
    )

    productCardEls.forEach(productCardEl => {
      this.observer.observe(productCardEl)
    })
  }

  onCollectionPaginationChange () {
    const paginationSearchParams = new URLSearchParams(window.location.search)
    const pageNumber = paginationSearchParams.get('page') ?? '1'

    const newPageProductCardEls = document.querySelectorAll(
      `.prd-List_Item[data-page="${pageNumber}"] [data-theme-ga4-el='product-card']`,
    )

    newPageProductCardEls.forEach(productCardEl => {
      this.observer.observe(productCardEl)
    })
  }

  trackProductCardViewEntry (entries, observer) {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return

      const intersectedProductCard = entry.target

      if (!intersectedProductCard.dataset?.themeGa4Url) return

      window.dataLayer.push({
        page_type: 'product_card',
        event: 'product_card_impression',
        product_card_url: `${intersectedProductCard.dataset.themeGa4Url}`,
      })

      observer.unobserve(intersectedProductCard)
    })
  }

  trackFilterItems () {
    this.listeners.add(
      document,
      'click',
      this.handleTrackFilterItems.bind(this),
    )
  }

  handleTrackFilterItems (event) {
    const trigger = event.target.closest(`[data-theme-ga4-el='filter']`)

    if (!trigger || event.target.tagName !== 'INPUT') return

    window.dataLayer.push({
      event: 'plp_filter_trigger',
      target_context: trigger.dataset.themeGa4Filter,
      target_value: trigger.dataset.themeGa4Value,
    })
  }

  trackQuickAdd (event) {
    const formNameEl = event.detail.form?.closest(
      '[data-ga4-quick-add-context]',
    )

    if (!formNameEl) return

    const { handle, title } = event.detail?.state ?? {}

    if (!handle || !title) return

    window.dataLayer.push({
      event: 'add_to_cart_form',
      target_context: formNameEl.getAttribute('data-ga4-quick-add-context'),
      item_handle: handle,
      item_title: title,
    })
  }

  trackRemoveFromCart () {
    const removeFromCartEls = document.querySelectorAll('[data-cart-item-el]')
    if (!removeFromCartEls.length) return

    removeFromCartEls.forEach(el => {
      el.addEventListener('click', this.handleRemoveFromCart.bind(this))
    })
  }

  handleRemoveFromCart (event) {
    const el = event.target
    try {
      window.dataLayer.push({
        event: 'remove_from_cart',
        product_name: el.dataset.ga4ProductTitle,
        product_quantity: el.dataset.ga4ProductQuantity,
        product_price: el.dataset.ga4ProductPrice,
        product_url: el.dataset.ga4ProductUrl,
      })
    } catch (error) {
      console.error('Error pushing to dataLayer: ', error)
    }
  }

  trackNewsletterSectionViews () {
    const newsletterEls = document.querySelectorAll('.sec-Newsletter_Form')

    if (!newsletterEls.length) return

    this.newsletterObserver = new IntersectionObserver(
      this.trackNewsletterEntryView.bind(this),
      {
        threshold: 0.8,
      },
    )

    newsletterEls.forEach(el => {
      this.newsletterObserver.observe(el)
    })
  }

  trackNewsletterEntryView (entries, observer) {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return

      window.dataLayer.push({
        page_type: 'newsletter',
        event: 'newsletter_impression',
      })

      observer.unobserve(entry.target)
    })
  }

  trackCustomerLogin () {
    const loginForms = document.querySelectorAll('#customer_login')

    if (!loginForms.length) return

    loginForms.forEach(form => {
      this.listeners.add(form, 'submit', this.handleCustomerLogin.bind(this))
    })
  }

  handleCustomerLogin (event) {
    window.dataLayer.push({
      page_type: 'login',
      event: 'login',
    })
  }

  trackPlpSort (event) {
    const targetValue = event.target?.value ?? event.target?.dataset?.value

    if (!targetValue) return

    window.dataLayer.push({
      event: 'plp_sort',
      target_value: targetValue,
    })
  }

  trackRecsInteractions (event) {
    const target = event.target
    const targetContext = target
      .closest('[data-ga4-recs-el]')
      .getAttribute('data-ga4-recs-el')
    const targetElement = this.getRecsTarget(target)

    const productCard = target.closest('[data-theme-ga4-el="product-card"]')
    const itemHandle = productCard
      ? productCard.getAttribute('data-theme-ga4-item-handle')
      : 'n/a'
    const itemTitle = productCard
      ? productCard.getAttribute('data-theme-ga4-item-title')
      : 'n/a'

    window.dataLayer.push({
      event: 'recs_interaction',
      target_context: targetContext,
      target_element: targetElement,
      item_handle: itemHandle,
      item_title: itemTitle,
    })
  }

  getRecsTarget (target) {
    const isArrow = target.closest('[data-theme-ga4-el="carousel-arrows"]')
    const isProductCard = target.closest('[data-theme-ga4-el="product-card"]')
    const isQuickAddForm = target.closest('form')
    const isQuickAddSubmit =
      isQuickAddForm && target.closest('button[type="submit"]')

    let recsTarget = null

    if (isArrow) {
      recsTarget = 'arrow'
    } else if (isQuickAddSubmit) {
      recsTarget = 'quick add submit'
    } else if (isProductCard && !isQuickAddForm) {
      recsTarget = 'product card'
    }

    return recsTarget
  }

  trackSearchItemRedirects () {
    const referrer = document.referrer

    if (!referrer.includes('search-results')) return

    const productHandle = document.location.pathname
      .split('products/')[1]
      ?.split('?')[0]

    window.dataLayer.push({
      event: 'search_result_click',
      item_handle: productHandle,
    })
  }

  trackDrawers (event) {
    const key = event.detail.key

    if (!key) return

    window.dataLayer.push({
      event: 'drawer_open',
      target_value: key,
    })
  }
}

if (!customElements.get('theme-ga4')) {
  customElements.define('theme-ga4', ThemeGa4)
}
